/**
 * Fixture data for the cabinet, typed against the generated schemas.
 *
 * EVERY CONSTANT BELOW IS ANNOTATED WITH ITS GENERATED TYPE, which is the point: a fixture that
 * type-checks is a fixture the real API could return, and a spec change breaks these files before
 * it breaks a screen. Nothing here is `as`-cast into shape.
 *
 * NO CLOCK IS READ ANYWHERE IN THIS APP, so every instant here is a literal. That is not a fixture
 * convenience — it is C11 (adr/0013, the lint rule bans `Date.now()` and `new Date()` across
 * web/) meeting D9: every figure the dashboard returns is bucketed on the UTC day resolved on the
 * SERVER clock, and a browser that decided its own "today" would disagree with the numbers it was
 * about to render. So ranges come from the response, never from the page.
 *
 * The values exercise states, not plausibility: a `not_reported_yet` tile beside an `ok` one, a
 * NEGATIVE balance, scheduled and staff-archived articles, an unanswered comment, an uncounted day.
 */
import type {
  ArticleListItem,
  ArticleStats,
  AuthorSession,
  CabinetComment,
  Dashboard,
  OperationResult,
  PaymentsView,
  Statement,
} from './generated';
import type { AgreementList, CountryList, PaymentDetails } from './cabinet-api';

/* ---------------------------------------------------------------------------------------------
 * Session.
 * ------------------------------------------------------------------------------------------- */

export const FIXTURE_SESSION: AuthorSession = {
  authorId: 'aut_0192f3c1',
  displayName: 'Vera Mikhailova',
  authorStatus: 'approved',
  applicationState: 'approved',
  canApply: false,
  canUseEditor: true,
  cabinetReachable: true,
  termsAcceptanceRequired: false,
  slug: 'vera-mikhailova',
  slugFrozen: true,
};

/**
 * The other session that must render: a disabled author. `cabinetReachable` STAYS TRUE — the
 * cabinet is reachable, the reason is shown, and only payout actions are refused (D34.1).
 */
export const FIXTURE_SESSION_DISABLED: AuthorSession = {
  authorId: 'aut_0192f3c1',
  displayName: 'Vera Mikhailova',
  authorStatus: 'disabled',
  applicationState: 'approved',
  canApply: false,
  canUseEditor: false,
  cabinetReachable: true,
  disabledReason:
    'On hold while the editors review a copyright report. The editors will write to you.',
  termsAcceptanceRequired: false,
  slug: 'vera-mikhailova',
  slugFrozen: true,
};

/* ---------------------------------------------------------------------------------------------
 * Dashboard.
 *
 * Four tiles are `not_reported_yet` at launch — shares, average reading time, read-through and
 * sources — because no shipped client emits the event: "an honest launch state, not a defect",
 * never drawn as a zero. Saves ARE reported (2026-10-05: old-app favourites came with their dates).
 *
 * THIS AUTHOR CAME OVER FROM THE OLD MOONX APPS (design 14, F2): ordinal 1 (`art_0192aa10`) and 2
 * (`art_0192e100`, taken down) had 1_800 old-app views. `allTimeViews` is every view ever, both
 * apps, one number (the owner, 2026-10-05): the period's 18_442, those 1_800 and 6_240 from before,
 * and no period scales it. The deprecated `oldAppViews` is left off: no screen shows it any more.
 * ------------------------------------------------------------------------------------------- */

export const FIXTURE_DASHBOARD: Dashboard = {
  from: '2026-07-29',
  to: '2026-08-26',
  allTimeViews: 26_482,
  tiles: {
    views: { state: 'ok', value: 18_442 },
    countedViews: { state: 'ok', value: 11_907 },
    readers: { state: 'ok', distinctReadersInRange: 9_318 },
    likes: { state: 'ok', value: 612 },
    comments: { state: 'ok', value: 74 },
    tips: { state: 'ok', value: 11 },
    supporterMessages: { state: 'ok', value: 4 },
    followers: { state: 'ok', value: 1_284, gainedInRange: 63, lostInRange: 9 },
    shares: { state: 'not_reported_yet' },
    saves: { state: 'ok', value: 143 },
    averageReadingTime: { state: 'not_reported_yet' },
    readThrough: { state: 'not_reported_yet' },
    sources: { state: 'not_reported_yet' },
  },
};

/** A brand-new author's first look: the range is genuinely empty and zero is TRUE. */
export const FIXTURE_DASHBOARD_NO_DATA: Dashboard = {
  from: '2026-08-20',
  to: '2026-08-26',
  allTimeViews: 0,
  tiles: {
    views: { state: 'no_data' },
    countedViews: { state: 'no_data' },
    readers: { state: 'no_data' },
    likes: { state: 'no_data' },
    comments: { state: 'no_data' },
    tips: { state: 'no_data' },
    supporterMessages: { state: 'no_data' },
    followers: { state: 'ok', value: 0 },
    shares: { state: 'not_reported_yet' },
    saves: { state: 'no_data' },
    averageReadingTime: { state: 'not_reported_yet' },
    readThrough: { state: 'not_reported_yet' },
    sources: { state: 'not_reported_yet' },
  },
};

/* ---------------------------------------------------------------------------------------------
 * Per-article statistics.
 *
 * BOTH VIEW NUMBERS ARE ON EVERY ROW, and `allTimeViews` - every view the article ever had - beside
 * them. `views` is the period's, `countedViews` the countable view that PAY uses. There is no
 * `distinctReaders` field on this schema at all — it is per-day and does not sum.
 * ------------------------------------------------------------------------------------------- */

export const FIXTURE_ARTICLE_STATS: readonly ArticleStats[] = [
  {
    articleId: 'art_0192f4aa',
    title: 'What the August full moon asks of you',
    publishedAt: '2026-08-09T06:00:00Z',
    publicationOrdinal: 7,
    importedFromOldApp: false,
    allTimeViews: 8_214,
    views: { state: 'ok', value: 8_214 },
    countedViews: { state: 'ok', value: 5_401 },
    likes: { state: 'ok', value: 288 },
    comments: { state: 'ok', value: 41 },
    shares: { state: 'not_reported_yet' },
    saves: { state: 'ok', value: 61 },
    averageReadingTime: { state: 'not_reported_yet' },
    readThrough: { state: 'not_reported_yet' },
  },
  {
    articleId: 'art_0192f2b7',
    title: 'Mercury retrograde is not an excuse',
    publishedAt: '2026-08-02T06:00:00Z',
    publicationOrdinal: 6,
    importedFromOldApp: false,
    allTimeViews: 6_930,
    views: { state: 'ok', value: 6_930 },
    countedViews: { state: 'ok', value: 4_512 },
    likes: { state: 'ok', value: 241 },
    comments: { state: 'ok', value: 26 },
    shares: { state: 'not_reported_yet' },
    saves: { state: 'ok', value: 48 },
    averageReadingTime: { state: 'not_reported_yet' },
    readThrough: { state: 'not_reported_yet' },
  },
  {
    articleId: 'art_0192ef01',
    title: 'A quiet guide to the ninth house',
    publishedAt: '2026-07-30T06:00:00Z',
    publicationOrdinal: 5,
    importedFromOldApp: false,
    allTimeViews: 3_298,
    views: { state: 'ok', value: 3_298 },
    countedViews: { state: 'ok', value: 1_994 },
    likes: { state: 'ok', value: 83 },
    comments: { state: 'ok', value: 7 },
    shares: { state: 'not_reported_yet' },
    saves: { state: 'ok', value: 17 },
    averageReadingTime: { state: 'not_reported_yet' },
    readThrough: { state: 'not_reported_yet' },
  },
  {
    // The unpaid first article. Ordinal 1 earns nothing, ever (D7) — the stats table shows it so a
    // zero beside it on the statement is explained rather than looking like a bug. This author's
    // ordinal 1 CAME OVER FROM THE OLD APP, so no screen calls it "your first article" (design 14,
    // F2); its all-time views are its 1_234 old-app views, the period's 1_106 and 1_072 between.
    articleId: 'art_0192aa10',
    title: 'Why I started writing about the sky',
    publishedAt: '2026-03-14T06:00:00Z',
    publicationOrdinal: 1,
    importedFromOldApp: true,
    allTimeViews: 3_412,
    views: { state: 'ok', value: 1_106 },
    countedViews: { state: 'ok', value: 702 },
    likes: { state: 'ok', value: 55 },
    comments: { state: 'ok', value: 3 },
    shares: { state: 'not_reported_yet' },
    saves: { state: 'ok', value: 9 },
    averageReadingTime: { state: 'not_reported_yet' },
    readThrough: { state: 'not_reported_yet' },
  },
  {
    // Published inside the range but before the projection caught up: every number is honestly
    // empty rather than zero.
    articleId: 'art_0192f5c9',
    title: 'The eclipse nobody will photograph',
    publishedAt: '2026-08-26T06:00:00Z',
    publicationOrdinal: 8,
    importedFromOldApp: false,
    allTimeViews: 0,
    views: { state: 'no_data' },
    countedViews: { state: 'no_data' },
    likes: { state: 'no_data' },
    comments: { state: 'no_data' },
    shares: { state: 'not_reported_yet' },
    saves: { state: 'no_data' },
    averageReadingTime: { state: 'not_reported_yet' },
    readThrough: { state: 'not_reported_yet' },
  },
];

/**
 * What `getArticleSummary` adds to a stats row: the tips sent on the article and, while it is
 * published, its path on the website. The counts are the row's own (fixture-cabinet-api.ts).
 */
export const FIXTURE_ARTICLE_SUMMARY_EXTRAS: ReadonlyMap<
  string,
  { readonly tipsMinor: number; readonly webPath: string | null }
> = new Map([
  [
    'art_0192f4aa',
    { tipsMinor: 1_250, webPath: '/magazine/moon/what-the-august-full-moon-asks-of-you' },
  ],
  [
    'art_0192f2b7',
    { tipsMinor: 600, webPath: '/magazine/astrology/mercury-retrograde-is-not-an-excuse' },
  ],
  [
    'art_0192f5c9',
    { tipsMinor: 0, webPath: '/magazine/astrology/the-eclipse-nobody-will-photograph' },
  ],
  // The editor desk's piece in review, as the specs that publish it read it.
  ['art_0192f5e2', { tipsMinor: 0, webPath: '/magazine/astrology/on-the-ethics-of-prediction' }],
]);

/* ---------------------------------------------------------------------------------------------
 * Articles.
 *
 * One row per status the author can act on, so every row action has something to act on.
 *
 * Ordinals 1 and 2 were brought over from the old MoonX apps (`importedFromOldApp`, design 14 F2):
 * ordinal 1 is badged as that rather than "Your first article", ordinal 2 keeps its "Your #2".
 * ------------------------------------------------------------------------------------------- */

export const FIXTURE_ARTICLES: readonly ArticleListItem[] = [
  {
    articleId: 'art_0192f5c9',
    status: 'published',
    title: 'The eclipse nobody will photograph',
    subtitle: 'And why that is the best thing about it',
    publishedAt: '2026-08-26T06:00:00Z',
    publicationOrdinal: 8,
    updatedAt: '2026-08-26T06:00:00Z',
    importedFromOldApp: false,
  },
  {
    articleId: 'art_0192f611',
    status: 'scheduled',
    title: 'Reading a chart you did not ask for',
    goLiveAt: '2026-09-02T06:00:00Z',
    updatedAt: '2026-08-25T14:12:00Z',
    importedFromOldApp: false,
  },
  {
    articleId: 'art_0192f5e2',
    status: 'in_review',
    title: 'On the ethics of prediction',
    updatedAt: '2026-08-24T09:41:00Z',
    importedFromOldApp: false,
  },
  {
    articleId: 'art_0192f4d3',
    status: 'changes_requested',
    title: 'Saturn returns, and so does everyone else',
    openBlockingFindings: 2,
    updatedAt: '2026-08-23T17:05:00Z',
    importedFromOldApp: false,
  },
  {
    articleId: 'art_0192f3ff',
    status: 'draft',
    title: 'Untitled draft',
    awaitingCover: true,
    updatedAt: '2026-08-22T11:30:00Z',
    importedFromOldApp: false,
  },
  {
    articleId: 'art_0192aa10',
    status: 'published',
    title: 'Why I started writing about the sky',
    publishedAt: '2026-03-14T06:00:00Z',
    publicationOrdinal: 1,
    updatedAt: '2026-03-14T06:00:00Z',
    importedFromOldApp: true,
  },
  {
    articleId: 'art_0192e7c4',
    status: 'archived',
    title: 'A piece I would write differently now',
    publishedAt: '2026-05-02T06:00:00Z',
    publicationOrdinal: 3,
    updatedAt: '2026-06-19T08:00:00Z',
    importedFromOldApp: false,
  },
  {
    articleId: 'art_0192e100',
    status: 'unpublished',
    title: 'Taken down while a source was checked',
    publishedAt: '2026-04-11T06:00:00Z',
    publicationOrdinal: 2,
    updatedAt: '2026-04-28T13:22:00Z',
    importedFromOldApp: true,
  },
];

/**
 * Articles this author may NOT un-archive, because staff archived them. The list exists so the
 * mock can answer 403 ARCHIVE_ACTOR_MISMATCH honestly rather than always succeeding — an action
 * that always works in the fixtures is an action whose refusal path was never drawn.
 */
export const FIXTURE_STAFF_ARCHIVED: readonly string[] = ['art_0192e7c4'];

/* ---------------------------------------------------------------------------------------------
 * Comments on my articles.
 *
 * There is no hidden comment here and there CANNOT be: hidden comments are absent from this list
 * exactly as they are from the public thread, so the cabinet cannot be used to discover what was
 * hidden. That is a ruled property of the surface, not a fixture choice.
 * ------------------------------------------------------------------------------------------- */

export const FIXTURE_COMMENTS: readonly CabinetComment[] = [
  {
    commentId: 'cmt_0192f6a1',
    articleId: 'art_0192f4aa',
    articleTitle: 'What the August full moon asks of you',
    parentId: null,
    body: 'I read this on the train and then read it again at home. The bit about not making the moon carry your decisions landed.',
    createdAt: '2026-08-26T19:02:00Z',
    author: 'A reader',
    isArticleAuthor: false,
    hasAuthorReply: false,
  },
  {
    commentId: 'cmt_0192f68f',
    articleId: 'art_0192f4aa',
    articleTitle: 'What the August full moon asks of you',
    parentId: null,
    body: 'Is the ninth-house piece you mentioned already up somewhere?',
    createdAt: '2026-08-26T12:44:00Z',
    author: 'A reader',
    isArticleAuthor: false,
    hasAuthorReply: true,
  },
  {
    commentId: 'cmt_0192f690',
    articleId: 'art_0192f4aa',
    articleTitle: 'What the August full moon asks of you',
    parentId: 'cmt_0192f68f',
    body: 'It is — I linked it at the end of the piece. Thank you for reading.',
    createdAt: '2026-08-26T13:10:00Z',
    author: 'Vera Mikhailova',
    isArticleAuthor: true,
    hasAuthorReply: false,
  },
  {
    commentId: 'cmt_0192f5b2',
    articleId: 'art_0192f2b7',
    articleTitle: 'Mercury retrograde is not an excuse',
    parentId: null,
    body: 'Finally somebody said it.',
    createdAt: '2026-08-25T08:15:00Z',
    author: 'A reader',
    isArticleAuthor: false,
    hasAuthorReply: false,
  },
  {
    commentId: 'cmt_0192f401',
    articleId: 'art_0192ef01',
    articleTitle: 'A quiet guide to the ninth house',
    parentId: null,
    body: 'Where would you send someone who wants to read more about this, but not a textbook?',
    createdAt: '2026-08-21T21:30:00Z',
    author: 'A reader',
    isArticleAuthor: false,
    hasAuthorReply: false,
  },
];

/* ---------------------------------------------------------------------------------------------
 * Payments.
 *
 * THE ARITHMETIC IS THE FIXTURE'S JOB TO GET RIGHT, because the obvious addition is wrong:
 * `projectedTotalMinor` is `availableMinor + articlesMinor` and NOTHING ELSE. This month's tips
 * are already inside `availableMinor`, so `tipsMinor` is a breakdown, never a third addend.
 *
 *   availableMinor      1_150   (all of it this month's tips - July's payout took the rest)
 *   articlesMinor      18_640
 *   projectedTotal     19_790   = 1_150 + 18_640
 *
 * AND THE BREAKDOWN ADDS UP TO `availableMinor`, EXACTLY (design 14, F14). It used to leave out the
 * ledger kinds that move a balance besides earnings - the opening balance carried in from the old
 * system, payouts approved, and failed payouts put back - so the parts could not reach the total.
 * With `openingBalanceMinor` and `payoutsMinor` they do:
 *
 *   openingBalanceMinor     320   carried in from the old system at the cutover, before May
 *   articleFeesMinor     18_110   May's 3_980 + July's 14_130 (June's one line was an award)
 *   tipsMinor             1_150   August's
 *   bonusesMinor              0
 *   awardsMinor           2_970   June's Editor's Choice
 *   correctionsMinor          0
 *   payoutsMinor        -21_400   July's run, paid (zero or negative, always)
 *                       -------
 *   availableMinor        1_150
 *
 * AND EVERY RUN KEEPS THE DATABASE'S OWN RULE (`payout_run_item_check`): closing = opening +
 * earnings + award, where earnings is the lines' settled amounts less their awards; a run's opening
 * is the ledger at its period's end, so it is the last closing carried forward plus whatever landed
 * since; and a payable run sends its whole closing. The runs, the statements and the breakdown are
 * one ledger, read three ways (fixed in design 14's wave - they disagreed, and the payments page
 * printed "Total €0.00" beside "Payout €214.00"):
 *
 *   run       opening   earnings   award   closing   what happened
 *   2026-05       320      3_980       0     4_300   under the 5_000 threshold - carried forward
 *   2026-06     4_300          0   2_970     7_270   no payout method on file - carried forward
 *   2026-07     7_270     14_130       0    21_400   payable, sent 21_400 on 6 August
 * ------------------------------------------------------------------------------------------- */

export const FIXTURE_PAYMENTS: PaymentsView = {
  currency: 'EUR',
  availableMinor: 1_150,
  breakdown: {
    openingBalanceMinor: 320,
    articleFeesMinor: 18_110,
    tipsMinor: 1_150,
    bonusesMinor: 0,
    awardsMinor: 2_970,
    correctionsMinor: 0,
    payoutsMinor: -21_400,
  },
  threshold: { thresholdMinor: 5_000, isMet: false },
  firstArticleUnpaid: false,
  currentMonth: {
    period: '2026-08',
    isFinal: false,
    articlesMinor: 18_640,
    tipsMinor: 1_150,
    projectedTotalMinor: 19_790,
    countedThrough: '2026-08-25',
    meetsThresholdProjected: true,
  },
  payouts: [
    {
      period: '2026-07',
      currency: 'EUR',
      earningsMinor: 14_130,
      awardMinor: 0,
      closingBalanceMinor: 21_400,
      thresholdMinor: 5_000,
      payable: true,
      amountMinor: 21_400,
      status: 'paid',
      paidAt: '2026-08-06T10:14:00Z',
    },
    {
      period: '2026-06',
      currency: 'EUR',
      earningsMinor: 0,
      awardMinor: 2_970,
      closingBalanceMinor: 7_270,
      thresholdMinor: 5_000,
      payable: false,
      blockReason: 'no_payout_method',
      amountMinor: null,
      status: null,
      paidAt: null,
    },
    {
      period: '2026-05',
      currency: 'EUR',
      earningsMinor: 3_980,
      awardMinor: 0,
      closingBalanceMinor: 4_300,
      thresholdMinor: 5_000,
      payable: false,
      amountMinor: null,
      status: null,
      paidAt: null,
    },
  ],
  computedAt: '2026-08-27T09:31:44Z',
};

/**
 * The one an author will send a support ticket about if it is drawn wrong: a NEGATIVE available
 * balance, from a refunded tip. It is a real deduction and is NEVER CLAMPED to zero. A new author:
 * nothing carried in, nothing paid out, so the parts are the tip alone and add up to -840.
 */
export const FIXTURE_PAYMENTS_NEGATIVE: PaymentsView = {
  currency: 'EUR',
  availableMinor: -840,
  negativeReason: 'A tip from 12 August was refunded to the reader after their card was disputed.',
  breakdown: {
    openingBalanceMinor: 0,
    articleFeesMinor: 0,
    tipsMinor: -840,
    bonusesMinor: 0,
    awardsMinor: 0,
    payoutsMinor: 0,
  },
  threshold: { thresholdMinor: 5_000, isMet: false },
  firstArticleUnpaid: true,
  currentMonth: {
    period: '2026-08',
    isFinal: false,
    articlesMinor: 0,
    tipsMinor: -840,
    projectedTotalMinor: -840,
    countedThrough: '2026-08-25',
    meetsThresholdProjected: false,
  },
  payouts: [],
  computedAt: '2026-08-27T09:31:44Z',
};

export const FIXTURE_STATEMENT_PERIODS: readonly string[] = ['2026-07', '2026-06', '2026-05'];

export const FIXTURE_STATEMENTS: Readonly<Record<string, Statement>> = {
  '2026-07': {
    period: '2026-07',
    currency: 'EUR',
    languageCode: 'en',
    renderedText:
      'Statement for July 2026. Three articles earned in this period. The first-article exclusion ' +
      'does not apply. Payment of EUR 214.00 was sent on 6 August 2026 to the payout method on ' +
      'file at that time.',
    templateRef: 'statement/en/v3',
    renderedAt: '2026-08-06T10:14:00Z',
    lines: [
      {
        articleId: 'art_0192f2b7',
        publicationOrdinal: 6,
        publishedAt: '2026-08-02T06:00:00Z',
        countableViews: 4_512,
        countableLikes: 241,
        countableComments: 26,
        computedAmountMinor: 9_820,
        settlement: 'earning',
        settledAmountMinor: 9_820,
        currency: 'EUR',
      },
      {
        articleId: 'art_0192ef01',
        publicationOrdinal: 5,
        publishedAt: '2026-07-30T06:00:00Z',
        countableViews: 1_994,
        countableLikes: 83,
        countableComments: 7,
        computedAmountMinor: 4_310,
        settlement: 'earning',
        settledAmountMinor: 4_310,
        currency: 'EUR',
      },
      {
        articleId: 'vid_0192ef40',
        kind: 'video',
        publicationOrdinal: 2,
        publishedAt: '2026-07-18T06:00:00Z',
        countableViews: 120,
        countableLikes: 9,
        countableComments: 2,
        computedAmountMinor: 0,
        settlement: 'earning',
        settledAmountMinor: 0,
        currency: 'EUR',
      },
      {
        articleId: 'art_0192aa10',
        publicationOrdinal: 1,
        publishedAt: '2026-03-14T06:00:00Z',
        countableViews: 702,
        countableLikes: 55,
        countableComments: 3,
        // The price is kept even on a line that was not paid: it is a ranking input.
        computedAmountMinor: 1_540,
        settlement: 'excluded_first_article',
        settledAmountMinor: 0,
        currency: 'EUR',
      },
    ],
    totals: {
      openingBalanceMinor: 7_270,
      earningsMinor: 14_130,
      awardMinor: 0,
      // Opening + earnings + award, BEFORE the payout - the payable run sends all of it.
      closingBalanceMinor: 21_400,
      amountMinor: 21_400,
    },
  },
  '2026-06': {
    period: '2026-06',
    currency: 'EUR',
    languageCode: 'en',
    renderedText:
      "Statement for June 2026. One article took the Editor's Choice award for the month. The " +
      "award is settled alone: the article's own computed price is a ranking input and is not " +
      'added to it. Nothing was sent because no payout method was on file, so the balance was ' +
      'carried forward.',
    templateRef: 'statement/en/v3',
    renderedAt: '2026-07-05T10:02:00Z',
    lines: [
      {
        articleId: 'art_0192e100',
        publicationOrdinal: 2,
        publishedAt: '2026-04-11T06:00:00Z',
        countableViews: 5_640,
        countableLikes: 190,
        countableComments: 18,
        // AN AWARD LINE SETTLES AT THE AWARD ALONE — never the award plus the computed fee.
        computedAmountMinor: 12_280,
        settlement: 'award',
        awardMinor: 2_970,
        settledAmountMinor: 2_970,
        currency: 'EUR',
      },
    ],
    totals: {
      // May's closing, carried forward: nothing was sent and nothing landed in between.
      openingBalanceMinor: 4_300,
      // The lines' settled amounts less their awards: the one line here IS the award.
      earningsMinor: 0,
      awardMinor: 2_970,
      closingBalanceMinor: 7_270,
      amountMinor: null,
    },
  },
  '2026-05': {
    period: '2026-05',
    currency: 'EUR',
    languageCode: 'en',
    renderedText:
      'Statement for May 2026. The closing balance did not reach the payout threshold in force, ' +
      'so nothing was sent and the balance was carried forward.',
    templateRef: 'statement/en/v3',
    renderedAt: '2026-06-04T09:48:00Z',
    lines: [
      {
        articleId: 'art_0192e7c4',
        publicationOrdinal: 3,
        publishedAt: '2026-05-02T06:00:00Z',
        countableViews: 1_820,
        countableLikes: 62,
        countableComments: 4,
        computedAmountMinor: 3_980,
        settlement: 'earning',
        settledAmountMinor: 3_980,
        currency: 'EUR',
      },
    ],
    totals: {
      // Carried in from the old system at the cutover - the ledger at the end of May.
      openingBalanceMinor: 320,
      earningsMinor: 3_980,
      awardMinor: 0,
      closingBalanceMinor: 4_300,
      amountMinor: null,
    },
  },
};

/** `isSet: false` is the EMPTY record, not an absent one — the 200 still returns an object. */
export const FIXTURE_PAYOUT_METHOD_UNSET: PaymentDetails = {
  isSet: false,
  complete: false,
  missing: [
    'legal_name',
    'country',
    'paypal_email',
    'details_true',
    'payout_terms',
    'payment_data_notice',
  ],
  requiredFrom: '2027-01-01',
};

/**
 * THE DEFAULT AUTHOR CAME OVER FROM THE OLD APP WITH A PAYPAL EMAIL AND NOTHING ELSE (design 25):
 * a country typed later, no legal name, no confirmation and neither payout document accepted - so
 * the record is not complete, and the reminders name the day it is needed by (Q1's 1 January 2027).
 * The mask is the server's (`maskPayoutEmail`); the address in full is for Payment details (D5).
 */
export const FIXTURE_PAYOUT_METHOD: PaymentDetails = {
  isSet: true,
  methodKind: 'paypal',
  recipientCountry: 'PL',
  legalName: null,
  paypal: { maskedEmail: 'v***@example.com', email: 'vera@example.com' },
  detailsConfirmedAt: null,
  complete: false,
  missing: ['legal_name', 'details_true', 'payout_terms', 'payment_data_notice'],
  requiredFrom: '2027-01-01',
  createdAt: '2026-02-02T08:00:00Z',
  updatedAt: '2026-06-30T16:20:00Z',
};

/* ---------------------------------------------------------------------------------------------
 * The account's own screens: where money goes, /settings and the notifications (wave 25b).
 * ------------------------------------------------------------------------------------------- */

/**
 * The profile as `/settings` reads it: `signs` and `earningsVisibility`. A birth date IS on file,
 * so the page opens on "on file" with two signs and the public switch off - and no rising sign,
 * because a rising sign needs a birth place and this author has not picked one (wave 26's field).
 */
export const FIXTURE_OWN_PROFILE: OperationResult<'getOwnProfile'> = {
  authorId: 'aut_0192f3c1',
  displayName: 'Vera Mikhailova',
  slug: 'vera-mikhailova',
  slugFrozen: true,
  visibility: { timezone: false },
  expertise: [],
  tags: [],
  links: [],
  signs: {
    birthDataOnFile: true,
    computedSignsPublic: false,
    sunSign: 'virgo',
    moonSign: 'pisces',
    risingSign: null,
    zodiacSystem: 'tropical',
  },
  // Private by default (authors-api.yaml, `setEarningsVisibility`), so an author who never chose
  // reads as off with no moment of change.
  earningsVisibility: { enabled: false, changedAt: null },
  // A fixture author who has answered, so the Overview's question is not on every screen under test.
  tipsVisibility: { enabled: true, changedAt: '2026-10-20T10:00:00.000Z' },
};

type NotificationRow = NonNullable<OperationResult<'listNotifications'>['items']>[number];

/**
 * The feed, newest first. Two unread rows on top; one of them `suppressed` - a tip that reached no
 * phone. The tips switch below is ON, so the page says it was not sent for want of a phone or an
 * address, not that the author switched it off (design 14, F9); a row is recorded either way.
 * No row carries an id: read state moves by a cut-off instant, a row's `createdAt`.
 */
export const FIXTURE_NOTIFICATIONS: readonly NotificationRow[] = [
  {
    categoryCode: 'payout_statement',
    createdAt: '2026-08-06T10:20:00Z',
    readAt: null,
    state: 'sent',
    languageCode: 'en',
    references: { statementPeriod: '2026-07' },
    deliveries: [
      {
        channel: 'email',
        outcome: 'accepted',
        emailEvent: 'email.delivered',
        attemptedAt: '2026-08-06T10:20:04Z',
      },
    ],
  },
  {
    categoryCode: 'tip_received',
    createdAt: '2026-08-05T18:02:00Z',
    readAt: null,
    state: 'suppressed',
    languageCode: 'en',
    references: {
      articleId: 'art_0192f2b7',
      articleTitle: 'Mercury retrograde is not an excuse',
      amountMinor: 500,
      amountCurrency: 'EUR',
    },
    deliveries: [],
  },
  {
    categoryCode: 'article_published',
    createdAt: '2026-08-02T06:00:00Z',
    readAt: '2026-08-02T07:15:00Z',
    state: 'sent',
    languageCode: 'en',
    references: {
      articleId: 'art_0192f2b7',
      articleTitle: 'Mercury retrograde is not an excuse',
    },
    deliveries: [{ channel: 'push', outcome: 'accepted', attemptedAt: '2026-08-02T06:00:02Z' }],
  },
  {
    categoryCode: 'article_changes_requested',
    createdAt: '2026-07-28T15:40:00Z',
    readAt: '2026-07-28T16:02:00Z',
    state: 'sent',
    languageCode: 'en',
    references: {
      articleId: 'art_0192f4d3',
      articleTitle: 'Saturn returns, and so does everyone else',
      reviewSeq: 1,
    },
    deliveries: [
      { channel: 'push', outcome: 'accepted', attemptedAt: '2026-07-28T15:40:03Z' },
      {
        channel: 'email',
        outcome: 'accepted',
        emailEvent: 'email.delivered',
        attemptedAt: '2026-07-28T15:40:03Z',
      },
    ],
  },
];

type PreferenceItem = OperationResult<'getNotificationPreferences'>['items'][number];

/**
 * One author-audience category as the platform seeds it (`notification_category`, last changed by
 * `0112`): which channels it supports, whether it is always-on, and - for an author who never chose -
 * every supported channel at its default, on unless `defaultOn` is false.
 */
function seededCategory(
  categoryCode: string,
  sortOrder: number,
  isAlwaysOn: boolean,
  channels: readonly ('push' | 'email')[],
  defaultOn = true,
): PreferenceItem {
  return {
    categoryCode,
    sortOrder,
    isAlwaysOn,
    inCabinetFeed: true,
    channels: channels.map((channel) => ({
      channel,
      enabled: defaultOn,
      defaultEnabled: defaultOn,
      usingDefault: true,
      updatedAt: null,
    })),
  };
}

/**
 * The preference matrix: the nineteen author categories in the catalogue's own order, as `0112`
 * left them (the owner's list, 15 October 2026) - seven always sent, six switchable and on by
 * default, six switchable and off by default.
 */
export const FIXTURE_NOTIFICATION_PREFERENCES: OperationResult<'getNotificationPreferences'> = {
  items: [
    seededCategory('application_approved', 1, true, ['push', 'email']),
    seededCategory('application_needs_more_info', 2, true, ['push', 'email']),
    seededCategory('article_published', 3, false, ['push', 'email'], false),
    seededCategory('article_scheduled', 4, false, ['push', 'email'], false),
    seededCategory('article_changes_requested', 5, false, ['push', 'email']),
    seededCategory('article_manual_review', 6, false, ['push', 'email'], false),
    seededCategory('article_go_live_moved', 7, false, ['push', 'email'], false),
    seededCategory('article_unpublished', 8, false, ['push', 'email']),
    seededCategory('tip_received', 9, false, ['push']),
    seededCategory('supporter_message_received', 10, false, ['push']),
    seededCategory('payout_statement', 12, false, ['email'], false),
    seededCategory('payout_sent', 13, false, ['push', 'email'], false),
    seededCategory('payout_failed', 14, true, ['email']),
    seededCategory('terms_reacceptance_required', 20, true, ['push', 'email']),
    seededCategory('author_disabled', 22, true, ['email']),
    seededCategory('payment_details_due_in_thirty_days', 23, false, ['push']),
    seededCategory('payment_details_due_in_seven_days', 24, false, ['push']),
    seededCategory('payout_held_for_payment_details', 25, true, ['push']),
    seededCategory('payment_details_changed', 26, true, ['push']),
  ],
};

/* ---------------------------------------------------------------------------------------------
 * What readers sent (register R-006).
 *
 * One message per state the screen has to draw: public with a tip and no answer yet; private and in
 * another language, with nothing translated; answered; hidden by the author; taken back by its
 * sender (a legacy message - its id is an UPPERCASE UUID, served exactly as stored); and hidden by
 * the editors with the author's answer already taken down. Each carries its sender's byline label
 * and nothing else about them - two with the neutral label, as a reader who chose no name reads -
 * and no reader id or handle: the platform carries none on a message (the owner, 2026-10-20).
 * The article line is joined at list time by the fixture client, as the server joins it.
 * ------------------------------------------------------------------------------------------- */

type SupporterMessageRow = NonNullable<OperationResult<'listSupporterMessages'>['items']>[number];

const SHOWN = { isPublic: true, isRetracted: false, hiddenByAuthor: false, hiddenByAdmin: false };

export const FIXTURE_SUPPORTER_MESSAGES: readonly SupporterMessageRow[] = [
  {
    supporterMessageId: 'sm_0192f6a1',
    body: 'Your piece on Mercury retrograde made me laugh and then call my sister. Thank you.',
    postedAt: '2026-08-24T19:12:00Z',
    initialPublic: true,
    supporterName: 'Maya L.',
    articleId: 'art_0192f2b7',
    tipAmountMinor: 500,
    tipCurrency: 'EUR',
    tipStatus: 'confirmed',
    visibility: SHOWN,
    reply: null,
    translations: [],
  },
  {
    supporterMessageId: 'sm_0192f69c',
    body: 'Спасибо за статью о возвращении Сатурна. Я перечитала её три раза.',
    postedAt: '2026-08-21T08:40:00Z',
    initialPublic: false,
    supporterName: 'Ольга',
    articleId: null,
    tipAmountMinor: null,
    tipCurrency: null,
    tipStatus: null,
    visibility: { ...SHOWN, isPublic: false },
    reply: null,
    translations: [],
  },
  {
    supporterMessageId: 'sm_0192f610',
    body: 'I read every one of these on the train home. Keep going.',
    postedAt: '2026-08-12T17:05:00Z',
    initialPublic: true,
    supporterName: 'Tom Becker',
    articleId: 'art_0192f4d3',
    tipAmountMinor: 300,
    tipCurrency: 'EUR',
    tipStatus: 'pending',
    visibility: SHOWN,
    reply: {
      body: 'Thank you - the train is where half of them were written.',
      postedAt: '2026-08-12T20:31:00Z',
      isRetracted: false,
      hiddenByAdmin: false,
    },
    translations: [],
  },
  {
    supporterMessageId: 'sm_0192f5f0',
    body: 'Not my sign, not my problem - but this one was good.',
    postedAt: '2026-08-09T11:48:00Z',
    initialPublic: true,
    supporterName: 'Wanderer',
    articleId: null,
    tipAmountMinor: 200,
    tipCurrency: 'EUR',
    tipStatus: 'refunded',
    visibility: { ...SHOWN, hiddenByAuthor: true },
    reply: null,
    translations: [],
  },
  {
    supporterMessageId: '5B2E8C1A-4F3D-4C8B-9A71-2D6E0F9B3C47',
    body: 'Wrote this in a hurry, taking it back.',
    postedAt: '2026-07-30T22:14:00Z',
    initialPublic: true,
    supporterName: 'Wanderer',
    articleId: null,
    tipAmountMinor: null,
    tipCurrency: null,
    tipStatus: null,
    visibility: { ...SHOWN, isRetracted: true },
    reply: null,
    translations: [],
  },
  {
    supporterMessageId: 'sm_0192f480',
    body: 'Some readers deserve to hear this, and so do you.',
    postedAt: '2026-07-18T09:02:00Z',
    initialPublic: true,
    supporterName: 'Jonas',
    articleId: 'art_0192ef01',
    tipAmountMinor: null,
    tipCurrency: null,
    tipStatus: null,
    visibility: { ...SHOWN, hiddenByAdmin: true },
    reply: {
      body: 'Thank you for writing.',
      postedAt: '2026-07-18T10:40:00Z',
      isRetracted: true,
      hiddenByAdmin: false,
    },
    translations: [],
  },
];

type StoredTranslation = NonNullable<SupporterMessageRow['translations']>[number];

/**
 * What the translation job would store, by message. The fixture translates nothing: a request for a
 * message listed here answers `queued` and the text appears on the next list read, as the platform
 * does it; a message not listed is stored with its own words, the honest result for text already
 * in the language asked for.
 */
export const FIXTURE_MESSAGE_TRANSLATIONS: Readonly<Record<string, StoredTranslation>> = {
  sm_0192f69c: {
    targetLanguage: 'en',
    body: 'Thank you for the piece on the Saturn return. I have read it three times.',
    sourceLanguage: 'ru',
    translatedAt: '2026-08-27T09:31:44Z',
  },
};

/* ---------------------------------------------------------------------------------------------
 * The monthly audience (register R-020).
 *
 * Sealed in the collapsed form, as the platform seals it: groups under the floor are one
 * `below_threshold` line, whose reader count is present only when it merged two or more groups
 * (decision-log row 219, item 3). July 2026 has a line of each kind - seven countries at 190 readers,
 * one age group with no number - and a dimension with none; June 2026 has two dimensions nobody told
 * the platform anything about. August 2026 is the month the fixture's dashboard is still in
 * (`FIXTURE_DASHBOARD.to`), so it is not sealed, and neither is anything before June.
 * ------------------------------------------------------------------------------------------- */

type AudienceBreakdownBody = OperationResult<'getAudienceBreakdown'>;
type AudienceDimensionCode = AudienceBreakdownBody['dimension'];

export const FIXTURE_AUDIENCE: Readonly<
  Record<string, Readonly<Record<AudienceDimensionCode, AudienceBreakdownBody>>>
> = {
  '2026-07-01': {
    zodiac: {
      month: '2026-07-01',
      dimension: 'zodiac',
      buckets: [
        { bucketCode: 'aries', label: 'Aries', readerCount: 412 },
        { bucketCode: 'taurus', label: 'Taurus', readerCount: 388 },
        { bucketCode: 'gemini', label: 'Gemini', readerCount: 356 },
        { bucketCode: 'cancer', label: 'Cancer', readerCount: 471 },
        { bucketCode: 'leo', label: 'Leo', readerCount: 520 },
        { bucketCode: 'virgo', label: 'Virgo', readerCount: 603 },
        { bucketCode: 'libra', label: 'Libra', readerCount: 344 },
        { bucketCode: 'scorpio', label: 'Scorpio', readerCount: 298 },
      ],
      suppressed: { bucketCode: 'below_threshold', bucketCount: 4, readerCount: 290 },
      sealedAt: '2026-08-01T02:10:00Z',
    },
    age_band: {
      month: '2026-07-01',
      dimension: 'age_band',
      buckets: [
        { bucketCode: '18_24', label: '18-24', readerCount: 210 },
        { bucketCode: '25_34', label: '25-34', readerCount: 845 },
        { bucketCode: '35_44', label: '35-44', readerCount: 690 },
        { bucketCode: '45_54', label: '45-54', readerCount: 402 },
        { bucketCode: '55_64', label: '55-64', readerCount: 188 },
      ],
      suppressed: { bucketCode: 'below_threshold', bucketCount: 1 },
      sealedAt: '2026-08-01T02:10:00Z',
    },
    country: {
      month: '2026-07-01',
      dimension: 'country',
      buckets: [
        { bucketCode: 'RU', label: 'Russia', readerCount: 1204 },
        { bucketCode: 'PL', label: 'Poland', readerCount: 640 },
        { bucketCode: 'ES', label: 'Spain', readerCount: 512 },
        { bucketCode: 'US', label: 'United States', readerCount: 488 },
        { bucketCode: 'DE', label: 'Germany', readerCount: 301 },
        { bucketCode: 'GB', label: 'United Kingdom', readerCount: 244 },
      ],
      suppressed: { bucketCode: 'below_threshold', bucketCount: 7, readerCount: 190 },
      sealedAt: '2026-08-01T02:10:00Z',
    },
    platform: {
      month: '2026-07-01',
      dimension: 'platform',
      buckets: [
        { bucketCode: 'ios', label: 'iOS app', readerCount: 1920 },
        { bucketCode: 'android', label: 'Android app', readerCount: 1310 },
        { bucketCode: 'web', label: 'Web', readerCount: 402 },
      ],
      sealedAt: '2026-08-01T02:10:00Z',
    },
  },
  '2026-06-01': {
    zodiac: {
      month: '2026-06-01',
      dimension: 'zodiac',
      buckets: [],
      sealedAt: '2026-07-01T02:05:00Z',
    },
    age_band: {
      month: '2026-06-01',
      dimension: 'age_band',
      buckets: [],
      sealedAt: '2026-07-01T02:05:00Z',
    },
    country: {
      month: '2026-06-01',
      dimension: 'country',
      buckets: [
        { bucketCode: 'RU', label: 'Russia', readerCount: 980 },
        { bucketCode: 'PL', label: 'Poland', readerCount: 512 },
        { bucketCode: 'ES', label: 'Spain', readerCount: 377 },
      ],
      suppressed: { bucketCode: 'below_threshold', bucketCount: 5, readerCount: 260 },
      sealedAt: '2026-07-01T02:05:00Z',
    },
    platform: {
      month: '2026-06-01',
      dimension: 'platform',
      buckets: [
        { bucketCode: 'ios', label: 'iOS app', readerCount: 1502 },
        { bucketCode: 'android', label: 'Android app', readerCount: 1044 },
      ],
      sealedAt: '2026-07-01T02:05:00Z',
    },
  },
};

/* ---------------------------------------------------------------------------------------------
 * The birth-place typeahead (wave 26).
 * ------------------------------------------------------------------------------------------- */

type BirthPlaceSuggestion = OperationResult<'searchBirthPlaces'>['birthPlaces'][number];

/**
 * The settlements the fixture desk's place search knows, each as the platform answers it: the three
 * `setBirthData` members and nothing else - no coordinates. Gomel is the geocoding audit's own
 * example record and the dev stub's one place (infra/compose.dev.yml, `GEO_PLACE`); the two Polish
 * cities give a search more than one answer, and two places one zone.
 */
export const FIXTURE_BIRTH_PLACES: readonly BirthPlaceSuggestion[] = [
  {
    birthPlaceId: '627907',
    birthPlace: 'Gomel, Homyel Region, Belarus',
    birthTimeZone: 'Europe/Minsk',
  },
  {
    birthPlaceId: '756135',
    birthPlace: 'Warsaw, Masovian Voivodeship, Poland',
    birthTimeZone: 'Europe/Warsaw',
  },
  {
    birthPlaceId: '3094802',
    birthPlace: 'Kraków, Lesser Poland Voivodeship, Poland',
    birthTimeZone: 'Europe/Warsaw',
  },
];

/**
 * The rising sign the fixture desk answers once a known place and a time are sent. The desk computes
 * nothing; the platform resolves the place server-side and reads the ascendant (`birth-place.resolver.ts`).
 */
export const FIXTURE_RISING_SIGN = 'capricorn';

/* ---------------------------------------------------------------------------------------------
 * The author's earlier months (design 17).
 *
 * The months before the payouts above began (the first run is May's), as their monthly reports
 * stated them, newest first - for the author's records and NEVER in the balance: `openingBalanceMinor`
 * already holds whatever was still due at the start. Each month carries its own currency, as the
 * reports did (they were in dollars), so the screen formats it from the month and never assumes
 * the balance's. Written at the foot so that the lines above, cited by number, keep theirs.
 *
 * One month of each state the screen draws:
 *
 *   month     status    carried in   articles   tips   total   lines
 *   2026-04   paid           1_650      4_270    900   6_820   an Editor's Choice, an article no
 *                                                              longer here (no title, comments not
 *                                                              stated), the first article
 *   2026-03   carried            0          0  1_650   1_650   the first article, not counted
 *   2026-02   unknown            0       null    350    null   none listed
 *
 * The `empty` and `negative-balance` authors have none, and the screen draws no section for them.
 * ------------------------------------------------------------------------------------------- */

export const FIXTURE_EARLIER_MONTHS: OperationResult<'listEarlierMonths'> = {
  months: [
    {
      period: '2026-04',
      currency: 'USD',
      payoutStatus: 'paid',
      articlesMinor: 4_270,
      tipsMinor: 900,
      carriedInMinor: 1_650,
      totalMinor: 6_820,
      articleCount: 2,
      articles: [
        {
          articleId: 'art_0192e100',
          title: 'Taken down while a source was checked',
          publishedOn: '2026-04-11',
          views: 1_480,
          likes: 63,
          comments: 9,
          earnedMinor: 4_000,
          counted: true,
          firstArticle: false,
          editorsChoice: true,
        },
        {
          articleId: null,
          title: null,
          publishedOn: '2026-04-20',
          views: 410,
          likes: 12,
          comments: null,
          earnedMinor: 270,
          counted: true,
          firstArticle: false,
          editorsChoice: false,
        },
        {
          articleId: 'art_0192aa10',
          title: 'Why I started writing about the sky',
          publishedOn: '2026-03-14',
          views: 3_920,
          likes: 141,
          comments: 22,
          earnedMinor: 0,
          counted: false,
          firstArticle: true,
          editorsChoice: false,
        },
      ],
    },
    {
      period: '2026-03',
      currency: 'USD',
      payoutStatus: 'carried',
      articlesMinor: 0,
      tipsMinor: 1_650,
      carriedInMinor: 0,
      totalMinor: 1_650,
      articleCount: 0,
      articles: [
        {
          articleId: 'art_0192aa10',
          title: 'Why I started writing about the sky',
          publishedOn: '2026-03-14',
          views: 2_210,
          likes: 96,
          comments: 12,
          earnedMinor: 0,
          counted: false,
          firstArticle: true,
          editorsChoice: false,
        },
      ],
    },
    {
      period: '2026-02',
      currency: 'USD',
      payoutStatus: 'unknown',
      articlesMinor: null,
      tipsMinor: 350,
      carriedInMinor: 0,
      totalMinor: null,
      articleCount: null,
      articles: [],
    },
  ],
};

/* ---------------------------------------------------------------------------------------------
 * The agreements and the country list (design 25, T22 and T24) - at the foot, so the lines above
 * keep their numbers.
 *
 * Four documents in their order (P9): the editorial standards and the author agreement block
 * sending an article for review; the payout terms and the payment data notice are part of complete
 * payment details. The default author accepted the first two and neither payout document, so
 * nothing holds back their articles and Payment details asks for both. The addresses are the
 * documents' pages on moonx.app (design 25, 3.1).
 * ------------------------------------------------------------------------------------------- */

export const FIXTURE_AGREEMENTS: AgreementList = {
  items: [
    {
      documentCode: 'editorial_standards',
      title: 'Editorial standards',
      blocks: 'submission',
      live: {
        versionLabel: '2026-10-11',
        effectiveFrom: '2026-10-11T00:00:00Z',
        contentUrl: 'https://moonx.app/legal/editorial-standards/2026-10-11',
        isMaterialChange: true,
      },
      accepted: {
        versionLabel: '2026-10-11',
        acceptedAt: '2026-10-11T09:12:00Z',
        contentUrl: 'https://moonx.app/legal/editorial-standards/2026-10-11',
      },
      owed: false,
      newerVersionWaiting: false,
    },
    {
      documentCode: 'creator_program',
      title: 'Creator programme terms',
      blocks: 'submission',
      live: {
        versionLabel: '2026-01',
        effectiveFrom: '2026-01-01T00:00:00Z',
        contentUrl: 'https://moonx.app/legal/creator-terms',
        isMaterialChange: true,
      },
      accepted: {
        versionLabel: '2026-01',
        acceptedAt: '2026-02-02T08:00:00Z',
        contentUrl: 'https://moonx.app/legal/creator-terms',
      },
      owed: false,
      newerVersionWaiting: false,
    },
    {
      documentCode: 'payout_terms',
      title: 'Payout terms',
      blocks: 'payout',
      live: {
        versionLabel: '2026-10-11',
        effectiveFrom: '2026-10-11T00:00:00Z',
        contentUrl: 'https://moonx.app/legal/payout-terms/2026-10-11',
        isMaterialChange: true,
      },
      accepted: null,
      owed: true,
      newerVersionWaiting: false,
    },
    {
      documentCode: 'payment_data_notice',
      title: 'How MoonX uses your payment details',
      blocks: 'payout',
      live: {
        versionLabel: '2026-10-11',
        effectiveFrom: '2026-10-11T00:00:00Z',
        contentUrl: 'https://moonx.app/legal/payment-data-notice/2026-10-11',
        isMaterialChange: true,
      },
      accepted: null,
      owed: true,
      newerVersionWaiting: false,
    },
  ],
};

/**
 * A slice of the 249 live `country` rows, with the ISO short names the platform serves - some of
 * which (`Russian Federation`, `Moldova, Republic of`) the cabinet names otherwise in American
 * English. Kept in the served order (by code).
 */
export const FIXTURE_COUNTRIES: CountryList = {
  items: [
    { code: 'AE', label: 'United Arab Emirates' },
    { code: 'AM', label: 'Armenia' },
    { code: 'BY', label: 'Belarus' },
    { code: 'CA', label: 'Canada' },
    { code: 'CZ', label: 'Czechia' },
    { code: 'DE', label: 'Germany' },
    { code: 'EE', label: 'Estonia' },
    { code: 'ES', label: 'Spain' },
    { code: 'FR', label: 'France' },
    { code: 'GB', label: 'United Kingdom' },
    { code: 'GE', label: 'Georgia' },
    { code: 'IL', label: 'Israel' },
    { code: 'IT', label: 'Italy' },
    { code: 'KZ', label: 'Kazakhstan' },
    { code: 'LT', label: 'Lithuania' },
    { code: 'LV', label: 'Latvia' },
    { code: 'MD', label: 'Moldova, Republic of' },
    { code: 'NL', label: 'Netherlands' },
    { code: 'PL', label: 'Poland' },
    { code: 'PT', label: 'Portugal' },
    { code: 'RS', label: 'Serbia' },
    { code: 'RU', label: 'Russian Federation' },
    { code: 'TR', label: 'Türkiye' },
    { code: 'UA', label: 'Ukraine' },
    { code: 'US', label: 'United States' },
  ],
};

/**
 * `GET /v1/application` for the desk's author, who is approved (design 25: the Overview reads the
 * application for an applicant's "Getting started"; an approved author's is decided). The nine
 * checklist rows in their order, with design 23's labels; the evidence row was left out.
 */
export const FIXTURE_APPLICATION: OperationResult<'getApplication'> = {
  state: 'approved',
  round: 1,
  submittedAt: '2026-07-02T09:14:00Z',
  completenessPercent: 95,
  completenessAtSubmit: 95,
  canSubmit: false,
  items: [
    {
      fieldCode: 'display_name',
      label: 'Author name',
      weight: 15,
      isRequiredToSubmit: true,
      isSatisfied: true,
    },
    {
      fieldCode: 'avatar',
      label: 'A profile photo',
      weight: 15,
      isRequiredToSubmit: true,
      isSatisfied: true,
    },
    {
      fieldCode: 'bio_long',
      label: 'Full bio',
      weight: 20,
      isRequiredToSubmit: true,
      isSatisfied: true,
    },
    {
      fieldCode: 'expertise',
      label: 'At least one main area',
      weight: 10,
      isRequiredToSubmit: true,
      isSatisfied: true,
    },
    {
      fieldCode: 'headline',
      label: 'Headline',
      weight: 10,
      isRequiredToSubmit: false,
      isSatisfied: true,
    },
    {
      fieldCode: 'bio_short',
      label: 'Short bio',
      weight: 10,
      isRequiredToSubmit: false,
      isSatisfied: true,
    },
    {
      fieldCode: 'about',
      label: 'Anything else',
      weight: 10,
      isRequiredToSubmit: false,
      isSatisfied: true,
    },
    {
      fieldCode: 'social_link',
      label: 'A link to your work',
      weight: 5,
      isRequiredToSubmit: false,
      isSatisfied: true,
    },
    {
      fieldCode: 'credential',
      label: 'Identity evidence',
      weight: 5,
      isRequiredToSubmit: false,
      isSatisfied: false,
    },
  ],
  decision: { outcome: 'approved', decidedAt: '2026-07-04T16:02:00Z', messageToApplicant: null },
};
